import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2 } from 'lucide-react';
import { getDeliveryAddresses, deleteDeliveryAddress, DeliveryAddress } from '@/services/partnerApi';
import { AddDeliveryAddressForm } from './AddDeliveryAddressForm';

export function DeliveryAddresses() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const loadAddresses = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await getDeliveryAddresses(token);
      setAddresses(response.items);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les adresses de livraison.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, [token]);

  const handleDelete = async (addressId: number) => {
    if (!token) return;

    try {
      await deleteDeliveryAddress(token, addressId);
      toast({
        title: "Adresse supprimée",
        description: "L'adresse de livraison a été supprimée avec succès.",
      });
      loadAddresses(); // Reload addresses
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'adresse. Elle pourrait être utilisée dans des commandes.",
        variant: "destructive",
      });
    }
  };

  const handleAddressAdded = () => {
    setAddModalOpen(false);
    loadAddresses(); // Reload addresses
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Adresses de livraison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Adresses de livraison</CardTitle>
        <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une adresse
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>Ajouter une adresse de livraison</DialogTitle>
            </DialogHeader>
            <AddDeliveryAddressForm onSuccess={handleAddressAdded} />
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {addresses.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            Aucune adresse de livraison trouvée.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {addresses.map((address) => (
              <Card key={address.id} className="relative">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{address.name}</CardTitle>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer l'adresse</AlertDialogTitle>
                          <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer l'adresse "{address.name}" ? 
                            Cette action ne peut pas être annulée.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(address.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>{address.street}</p>
                    <p>
                      {address.city}, {address.state_name}, {address.zip_code}
                    </p>
                    <p>{address.country_name}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}